import { apiClient } from 'utils/apiClient';
import { ApiEndpoints } from 'models/apiEndpoints';
import { isResponseError, TResponse } from 'models/apiClient';
import {
  ILicencesTemplateResponse,
  ILicenceTemplateApplicationProcess,
  ILicenceTemplateApplicationProcessDocumentRequest,
  ILicenceTemplateApplicationProcessDocumentsResponse,
  ILicenceTemplateApplicationProcessIntelligenceReport,
  ILicenceTemplateApplicationProcessIntelligenceReportForm,
  ILicenceTemplateApplicationProcessIntelligenceReportRequest,
  ILicenceTemplateApplicationProcessIntelligenceReportsResponse,
  ILicenceTemplateApplicationProcessPriceUpdate,
  ILicenceTemplateApplicationProcessResponse,
  ILicenceTemplateApplicationProcessSettingsUpdate,
  ILicenceTemplateApplicationProcessTaskRequest,
  ILicenceTemplateApplicationProcessTasksResponse,
  ILicenceTemplatePrintoutSettingsAvailableFields,
  ILicenceTemplateProcessTypes,
  ILicenceTemplateRequest,
  ILicenceTemplateResponse,
  ILicenceTemplatesForLe,
  IPrintoutSettings,
  ISearchForm,
} from 'models/licenceConfigurator';
import { EPerPages, ISortOptions } from 'models/table';
import { queryStringWithSort } from 'helpers/request';
import { clearObject } from 'helpers/objects';

const getLicenceTemplates = async (
  currentPage: number,
  perPage: EPerPages,
  query: Partial<ISearchForm>,
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: currentPage - 1,
      size: perPage,
    },
    sort,
  );

  const { data } = await apiClient.post<TResponse<ILicencesTemplateResponse>>(
    `${ApiEndpoints.GET_LICENCE_TEMPLATES()}?${qString}`,
    clearObject(query),
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};
const getLicenceTemplatesForLE = async (
  body: object,
  currentPage: number,
  perPage: EPerPages,
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: currentPage - 1,
      size: perPage,
    },
    sort,
  );

  const { data } = await apiClient.post<TResponse<ILicenceTemplatesForLe>>(
    `${ApiEndpoints.GET_LICENCE_TEMPLATES_FOR_LE()}?${qString}`,
    body,
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const createLicenceTemplateApplicationProcess = async (
  body: ILicenceTemplateApplicationProcess,
) =>
  apiClient.post<TResponse<any>>(
    ApiEndpoints.CREATE_LICENCE_TEMPLATE_APPLICATION_PROCCESS(),
    body,
  );

const editLicenceTemplateApplicationProcessSettingsData = async (
  id: string,
  body: ILicenceTemplateApplicationProcessSettingsUpdate,
) =>
  apiClient.patch<TResponse<ILicenceTemplateRequest>>(
    ApiEndpoints.UPDATE_LICENCE_TEMPLATE_APPLICATION_PROCCESS_SETTINGS(id),
    body,
  );
const editLicenceTemplateApplicationProcessPriceData = async (
  id: string,
  body: ILicenceTemplateApplicationProcessPriceUpdate,
) =>
  apiClient.patch<TResponse<ILicenceTemplateRequest>>(
    ApiEndpoints.UPDATE_LICENCE_TEMPLATE_APPLICATION_PROCCESS_PRICE(id),
    body,
  );

const deactivateApplicationProccess = async (id: string) =>
  await apiClient.post(ApiEndpoints.DEACTIVATE_LICENCE_TEMPLATE_APPLICATION_PROCCESS(id));
const activateApplicationProccess = async (id: string) =>
  await apiClient.post(ApiEndpoints.ACTIVATE_LICENCE_TEMPLATE_APPLICATION_PROCCESS(id));

const deleteLicenceApplicationProcess = async (id: string): Promise<boolean> => {
  const { data } = await apiClient.delete(
    ApiEndpoints.DELETE_LICENCE_TEMPLATE_APPLICATION_PROCCESS(id),
  );

  return !isResponseError(data);
};

const createLicenceTemplate = async (body: any) =>
  apiClient.post<TResponse<ILicenceTemplateRequest>>(
    ApiEndpoints.CREATE_LICENCE_TEMPLATE(),
    body,
  );

const editLicenceTemplateData = async (body: any) =>
  apiClient.put<TResponse<ILicenceTemplateRequest>>(
    ApiEndpoints.MANAGE_LICENCE_TEMPLATE(),
    body,
  );

const deleteLicence = async (id: string): Promise<boolean> => {
  const { data } = await apiClient.delete(ApiEndpoints.DELETE_LICENCE_TEMPLATE(id));

  return !isResponseError(data);
};

const getLicenceTemplate = async (id: string): Promise<ILicenceTemplateResponse | null> => {
  const { data } = await apiClient.get(ApiEndpoints.GET_LICENCE_TEMPLATE(id));

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getLicenceTemplateApplicationProccess = async (
  id: string,
): Promise<ILicenceTemplateApplicationProcessResponse[]> => {
  const { data } = await apiClient.get(
    ApiEndpoints.GET_LICENCE_TEMPLATE_APPLICATION_PROCCESSES(id),
  );

  if (isResponseError(data)) {
    return [];
  }

  return data;
};
const getLicenceTemplatePriceApplicationProccessDetail = async (
  id: string,
): Promise<ILicenceTemplateApplicationProcessResponse | null> => {
  const { data } = await apiClient.get(
    ApiEndpoints.GET_LICENCE_TEMPLATE_APPLICATION_PROCCESS(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getLicenceTemplateProcessTypes = async (
  id: string,
): Promise<ILicenceTemplateProcessTypes | null> => {
  const { data } = await apiClient.get(
    ApiEndpoints.GET_LICENCE_TEMPLATE_APPLICATION_PROCESS_TYPES(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const changeLicenceTemplateStatus = async (id: string, isActiveStatus: boolean) => {
  const { data } = isActiveStatus
    ? await apiClient.post(ApiEndpoints.DEACTIVATE_LICENCE_TEMPLATE_STATUS(id))
    : await apiClient.post(ApiEndpoints.ACTIVATE_LICENCE_TEMPLATE_STATUS(id));

  if (isResponseError(data)) {
    return null;
  }

  return true;
};

const getApplicationProccessTask = async (
  id: string,
  currentPage: number,
  perPage: EPerPages,
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: currentPage - 1,
      size: perPage,
    },
    sort,
  );

  const { data } = await apiClient.get<
    TResponse<ILicenceTemplateApplicationProcessTasksResponse>
  >(`${ApiEndpoints.GET_APPLICATION_PROCCESSES_TASKS(id)}?${qString}`);
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const createApplicationProccessTask = async (
  body: ILicenceTemplateApplicationProcessTaskRequest,
) => apiClient.post<TResponse<any>>(ApiEndpoints.CREATE_APPLICATION_PROCCESSES_TASK(), body);

const deleteApplicationProccessTask = async (id: string): Promise<boolean> => {
  const { data } = await apiClient.delete(ApiEndpoints.DELETE_APPLICATION_PROCCESSES_TASK(id));

  return !isResponseError(data);
};

const getApplicationProccessDocument = async (
  id: string,
  currentPage: number,
  perPage: EPerPages,
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: currentPage - 1,
      size: perPage,
    },
    sort,
  );

  const { data } = await apiClient.get<
    TResponse<ILicenceTemplateApplicationProcessDocumentsResponse>
  >(`${ApiEndpoints.GET_APPLICATION_PROCCESSES_DOCUMENTS(id)}?${qString}`);
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const createApplicationProccessDocument = async (
  body: ILicenceTemplateApplicationProcessDocumentRequest,
) =>
  apiClient.post<TResponse<any>>(ApiEndpoints.CREATE_APPLICATION_PROCCESSES_DOCUMENT(), body);

const deleteApplicationProccessDocument = async (id: string): Promise<boolean> => {
  const { data } = await apiClient.delete(
    ApiEndpoints.DELETE_APPLICATION_PROCCESSES_DOCUMENT(id),
  );

  return !isResponseError(data);
};

const getApplicationProccessIntelligenceReport = async (
  id: string,
  currentPage: number,
  perPage: EPerPages,
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: currentPage - 1,
      size: perPage,
    },
    sort,
  );

  const { data } = await apiClient.get<
    TResponse<ILicenceTemplateApplicationProcessIntelligenceReportsResponse>
  >(`${ApiEndpoints.GET_APPLICATION_PROCCESSES_INTELIGENCE_REPORTS(id)}?${qString}`);
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const createApplicationProccessIntelligenceReport = async (
  body: ILicenceTemplateApplicationProcessIntelligenceReportRequest,
) =>
  apiClient.post<TResponse<any>>(
    ApiEndpoints.CREATE_APPLICATION_PROCCESSES_INTELIGENCE_REPORT(),
    body,
  );

const updateApplicationProccessIntelligenceReport = async (
  id: string,
  body: ILicenceTemplateApplicationProcessIntelligenceReportForm,
) =>
  apiClient.patch<TResponse<any>>(
    ApiEndpoints.UPDATE_APPLICATION_PROCCESSES_INTELIGENCE_REPORT(id),
    body,
  );

const updatePrintoutSettings = async (id: string, body: IPrintoutSettings) =>
  apiClient.patch<TResponse<any>>(ApiEndpoints.UPDATE_PRINTOUT_SETTINGS(id), body);
const createPrintoutSettings = async (id: string, body: IPrintoutSettings) =>
  apiClient.post<TResponse<any>>(ApiEndpoints.CREATE_PRINTOUT_SETTINGS(id), body);

const deleteApplicationProccessIntelligenceReport = async (id: string): Promise<boolean> => {
  const { data } = await apiClient.delete(
    ApiEndpoints.DELETE_APPLICATION_PROCCESSES_INTELIGENCE_REPORT(id),
  );

  return !isResponseError(data);
};

const getPrintoutSettingsAvailableFields = async (
  id: string,
): Promise<ILicenceTemplatePrintoutSettingsAvailableFields | null> => {
  const { data } = await apiClient.get(
    ApiEndpoints.GET_PRINTOUT_SETTINGS_AVAILABLE_FIELDS(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

export {
  getLicenceTemplates,
  createLicenceTemplate,
  getLicenceTemplate,
  editLicenceTemplateData,
  deleteLicence,
  changeLicenceTemplateStatus,
  getLicenceTemplatesForLE,
  getLicenceTemplateApplicationProccess,
  createLicenceTemplateApplicationProcess,
  editLicenceTemplateApplicationProcessSettingsData,
  editLicenceTemplateApplicationProcessPriceData,
  getLicenceTemplateProcessTypes,
  getLicenceTemplatePriceApplicationProccessDetail,
  deleteLicenceApplicationProcess,
  deactivateApplicationProccess,
  activateApplicationProccess,
  createApplicationProccessTask,
  getApplicationProccessTask,
  deleteApplicationProccessTask,
  getApplicationProccessDocument,
  createApplicationProccessDocument,
  deleteApplicationProccessDocument,
  getApplicationProccessIntelligenceReport,
  createApplicationProccessIntelligenceReport,
  updateApplicationProccessIntelligenceReport,
  deleteApplicationProccessIntelligenceReport,
  updatePrintoutSettings,
  createPrintoutSettings,
  getPrintoutSettingsAvailableFields,
};
